import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { setStoredActiveOrgSlug } from '../active-org.ts';
import { useInstallationsPolling } from '../api.ts';

import { Layout } from './Layout.tsx';

const POLL_INTERVAL_MS = 1500;

/**
 * Post-callback landing. The user just finished installing the GitHub
 * App. We pin active org to the slug from the redirect, then poll the
 * org's installations until the new id appears (TanStack Query handles
 * the interval + stop-on-found via `refetchInterval`). Once visible,
 * route back to /.
 */
export function Connected() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orgSlug = params.get('org_slug');
  const installationIdParam = params.get('installation_id');
  const errorParam = params.get('error');
  const noteParam = params.get('note');

  const targetInstallationId = installationIdParam ? Number(installationIdParam) : null;

  useEffect(() => {
    if (!orgSlug) return;
    setStoredActiveOrgSlug(orgSlug);
  }, [orgSlug]);

  const polling = useInstallationsPolling({
    orgSlug,
    targetInstallationId,
    enabled: !errorParam && !!orgSlug && !!targetInstallationId,
    pollMs: POLL_INTERVAL_MS,
  });

  useEffect(() => {
    if (polling.found) {
      navigate('/?connected=1', { replace: true });
    }
  }, [polling.found, navigate]);

  if (errorParam) {
    return (
      <Layout title="GitHub install — Technical Accounting">
        <h1 className="view-h1">Something went wrong</h1>
        <p style={{ color: '#c0392b' }}>{errorMessage(errorParam)}</p>
        <p>
          <a href="/" className="btn">
            Back to your org
          </a>
        </p>
      </Layout>
    );
  }

  if (!installationIdParam) {
    return (
      <Layout title="GitHub install — Technical Accounting">
        <h1 className="view-h1">Install received</h1>
        <p>It looks like you came here without an installation reference. Head back to your org to continue.</p>
        <p>
          <a href="/" className="btn">
            Back to your org
          </a>
        </p>
      </Layout>
    );
  }

  return (
    <Layout title="Connecting GitHub — Technical Accounting">
      <h1 className="view-h1">Wrapping up the GitHub install</h1>
      <p className="muted">
        We&apos;re linking installation <code>{installationIdParam}</code>
        {orgSlug ? <> to org <code>{orgSlug}</code></> : null}…
      </p>
      {noteParam === 'no_state' ? (
        <p className="muted">
          Heads up: this looks like an admin-approval install. The link should land within a few seconds via the webhook.
        </p>
      ) : null}
      {polling.isError ? (
        <p style={{ color: '#c0392b' }}>{polling.error.message}</p>
      ) : null}
      <p className="muted">
        {polling.data
          ? `${polling.data.length} installation${polling.data.length === 1 ? '' : 's'} visible so far.`
          : null}
      </p>
    </Layout>
  );
}

function errorMessage(code: string): string {
  switch (code) {
    case 'missing_installation_id':
      return 'GitHub did not return an installation id.';
    case 'invalid_installation_id':
      return 'GitHub returned an installation id we couldn\'t parse.';
    case 'expired':
      return 'The install link expired. Please start over.';
    case 'bad-signature':
    case 'invalid-payload':
    case 'malformed':
      return 'The install link was tampered with or malformed. Please start over.';
    case 'github_fetch_failed':
      return 'We couldn\'t reach GitHub to confirm the install. Please try again.';
    case 'unclaimed_install':
      return 'Install request expired before it could be reconciled. Please start over from the app.';
    default:
      return code;
  }
}
